import { providerRegistry } from './oauth/providerRegistry';

export interface OAuthOptions {
  redirectTo?: string;
}

/**
 * Normalize origin by removing 'www.' prefix to ensure consistency
 * with OAuth provider configurations
 */
function normalizeOrigin(origin: string): string {
  try {
    const url = new URL(origin);
    // Remove 'www.' from hostname if present
    if (url.hostname.startsWith('www.')) {
      url.hostname = url.hostname.substring(4);
    }
    return url.origin;
  } catch {
    // Fallback if URL parsing fails
    return origin.replace(/^https?:\/\/www\./, (match) => match.replace('www.', ''));
  }
}

/**
 * Generic OAuth sign-in handler that works with any provider from the registry
 */
export async function signInWithOAuth(
  providerId: string,
  options?: OAuthOptions
): Promise<{ error: Error | null }> {
  try {
    // Get provider from registry
    const provider = providerRegistry.getProvider(providerId);
    
    if (!provider) {
      return {
        error: new Error(`OAuth provider "${providerId}" is not available. Please check your configuration.`),
      };
    }

    // Generate state for CSRF protection
    const state = generateState();
    
    // Build redirect URI (normalize to remove www for consistency with OAuth config)
    let redirectUri: string;
    if (options?.redirectTo) {
      // Normalize custom redirect URI
      try {
        const url = new URL(options.redirectTo);
        url.hostname = url.hostname.startsWith('www.') ? url.hostname.substring(4) : url.hostname;
        redirectUri = url.toString();
      } catch {
        redirectUri = options.redirectTo;
      }
    } else {
      redirectUri = `${normalizeOrigin(window.location.origin)}/auth/callback`;
    }
    
    // Build authorization URL using provider
    const authUrl = provider.buildAuthorizationUrl(redirectUri, state);
    
    // Store state in both sessionStorage and localStorage for redundancy
    // localStorage persists across redirects better
    sessionStorage.setItem(`oauth_state_${providerId}`, state);
    localStorage.setItem(`oauth_state_${providerId}`, state);
    sessionStorage.setItem(`oauth_provider`, providerId);
    localStorage.setItem(`oauth_provider`, providerId);
    
    // Redirect to OAuth provider
    window.location.href = authUrl;
    
    // Return success (redirect will happen)
    return { error: null };
  } catch (error) {
    return {
      error: error instanceof Error ? error : new Error('Failed to initiate OAuth sign-in'),
    };
  }
}

/**
 * Generate a random state token for CSRF protection
 */
function generateState(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Verify OAuth state token (used in callback)
 */
export function verifyOAuthState(providerId: string, state: string): boolean {
  // Check both sessionStorage and localStorage
  const storedState = sessionStorage.getItem(`oauth_state_${providerId}`) || 
                      localStorage.getItem(`oauth_state_${providerId}`);
  
  if (!storedState) {
    console.warn('No stored state found for provider:', providerId);
    // For development, we might want to be more lenient
    // In production, this should be strict
    if (import.meta.env.DEV) {
      console.warn('Allowing OAuth flow in development mode without state verification');
      // Still clean up
      sessionStorage.removeItem(`oauth_state_${providerId}`);
      localStorage.removeItem(`oauth_state_${providerId}`);
      return true; // Allow in dev mode
    }
    return false;
  }
  
  const isValid = storedState === state;
  
  // Clean up after verification
  sessionStorage.removeItem(`oauth_state_${providerId}`);
  localStorage.removeItem(`oauth_state_${providerId}`);
  sessionStorage.removeItem(`oauth_provider`);
  localStorage.removeItem(`oauth_provider`);
  
  if (!isValid) {
    console.warn('State mismatch:', { stored: storedState, received: state });
  }
  
  return isValid;
}

/**
 * Get stored provider ID (used in callback)
 */
export function getStoredProviderId(): string | null {
  return sessionStorage.getItem(`oauth_provider`) || localStorage.getItem(`oauth_provider`);
}
